import { useEffect, useRef, useState } from 'preact/hooks'
import { FieldText } from './FieldText'
import { FieldCode } from './FieldCode'
import { AccountFitClubCard } from './AccountFitClubCard'
import { useFormInput } from '../hooks/useFormInput'
import { useSignUpDetails } from '../hooks/useSignUpDetails'
import register from 'preact-custom-element'
import { FieldTel } from './FieldTel'
import { FieldEmail } from './FieldEmail'
import { FieldConsent } from './FieldConsent'
import LoaderSpin from '@/components/snippets/loader-spin/loader-spin'
import { formatMobile } from '@/components/helpers/utils'
import { accountTranslations } from '../helpers/translations'
import { PageBackButton } from './AccountSectionWrapper'
import Icon from '@/components/snippets/icon/icon'
import { resolveLoginRedirect } from '@/components/helpers/login-redirect'
import { resolveFlowPreview } from '../helpers/flow-preview'

const ACCOUNT_SIGN_IN_STATES = {
  INIT: 'INIT',
  MOBILE_CODE: 'MOBILE_CODE',
  DETAILS: 'DETAILS',
  SUCCESS: 'SUCCESS'
}

/**
 * login_requested fires when a code/magic-link is sent to an existing customer.
 * The actual login completion is tracked separately in account-portal.liquid,
 * since loginWithSMS/mobileLogin and the email magic-link both finish via a
 * redirect back to the site rather than a JS callback on this page.
 *
 * Routed through BCDataLayerManager.manager.push() rather than a raw
 * window.dataLayer.push() so it gets the same brand event prefix (e.g. bc_)
 * and event_id/event_timestamp fields every other custom event gets - these
 * fire on user interaction, well after page load, so the manager is reliably
 * available by then.
 */
const pushDataLayer = (eventName, data) => {
  window.BCDataLayerManager?.manager?.push(eventName, data)
}

const SIGN_IN_CHANNELS = {
  EMAIL: 'email',
  MOBILE: 'mobile'
}

/** Matches the cooldown the app uses on the same endpoint. */
const RESEND_COOLDOWN_SECONDS = 60

const portalCopy = accountTranslations.account_portal

/** Fills `{token}` placeholders in a locale string. */
const fill = (template, tokens) =>
  Object.entries(tokens).reduce((text, [key, value]) => text.split(`{${key}}`).join(value), template || '')

/** Copy for one of the AuthErrorCode values `portal.js` returns. */
const authErrorMessage = (code) => portalCopy.auth_errors?.[code] || portalCopy.auth_errors?.unknown || ''

/**
 * Email and mobile are different journeys at Omneo - email sends a magic link, mobile sends a
 * 6 digit code - so the shopper picks one rather than the single field guessing from an "@".
 * There is no email equivalent of the SMS code endpoint, which is why the two cannot be merged.
 */
const SignInChannelToggle = ({ channel, onChange, disabled }) => (
  <div className={`flex w-full items-center justify-center ${disabled ? 'opacity-60' : 'opacity-100'}`}>
    <div
      className={
        'flex w-fit flex-row items-center justify-center gap-2 rounded-default border border-solid border-neutrals-medium-grey p-1'
      }
      role={'tablist'}
    >
      {[
        [SIGN_IN_CHANNELS.EMAIL, portalCopy.sign_in_channel_email],
        [SIGN_IN_CHANNELS.MOBILE, portalCopy.sign_in_channel_mobile]
      ].map(([value, label]) => (
        <button
          key={value}
          type={'button'}
          role={'tab'}
          disabled={disabled}
          aria-selected={channel === value}
          data-ui={channel === value ? 'selected' : ''}
          data-channel={value}
          onClick={() => onChange(value)}
          className={`paragraph-default rounded-default border border-solid px-3 py-1 font-bold ${
            channel === value ? 'opacity-100' : 'opacity-60'
          }`}
        >
          {label}
        </button>
      ))}
    </div>
  </div>
)

const AccountMobileCodeValidation = ({ props }) => {
  const { identity } = props.config
  /* `data-resend-seconds` on the host element; the harness sets it low to exercise Resend. */
  const resendSeconds = Number(props.dataResendSeconds) || RESEND_COOLDOWN_SECONDS
  const [code, setCode] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(null)
  const [notice, setNotice] = useState(null)
  const [cooldown, setCooldown] = useState(resendSeconds)

  useEffect(() => {
    if (cooldown <= 0) return undefined
    const timer = setTimeout(() => setCooldown((seconds) => seconds - 1), 1000)
    return () => clearTimeout(timer)
  }, [cooldown])

  const submitCode = async (value) => {
    if (submitting || value.length !== 6) return
    setSubmitting(true)
    setError(null)
    setNotice(null)

    const { ok, token, error: errorCode } = await window.omneo.portal.loginWithSMS(value, identity.formatted)
    if (!ok) {
      setSubmitting(false)
      setError(authErrorMessage(errorCode))
      return
    }
    /* The browser navigates to Omneo from here, so `submitting` is deliberately left set. */
    window.omneo.portal.mobileLogin(token, resolveLoginRedirect())
  }

  /**
   * Autofill and paste both arrive as one input event, so completing the field is the submit -
   * without it a one-tap SMS autofill still needs a button press.
   */
  const handleInput = (event) => {
    const digits = event.currentTarget.value.replace(/\D/g, '').slice(0, 6)
    setCode(digits)
    if (digits.length === 6) submitCode(digits)
  }

  const handleResend = async () => {
    if (cooldown > 0 || submitting) return
    setError(null)
    setNotice(null)

    const { ok, error: errorCode } = await window.omneo.portal.getSMSAuthCode(identity.formatted)
    if (!ok) {
      setError(authErrorMessage(errorCode))
      /* A refused send still counts against the rate limit, so hold the cooldown either way. */
      setCooldown(resendSeconds)
      return
    }
    setCode('')
    setCooldown(resendSeconds)
    setNotice(portalCopy.sign_up_mobile_code_resent)
  }

  return (
    <form
      data-ui={submitting ? 'visible' : ''}
      onSubmit={(event) => {
        event.preventDefault()
        submitCode(code)
      }}
      className={'relative flex w-full flex-col gap-4 p-2 transition-opacity group-data-visible/login:opacity-45'}
    >
      <div className={'flex flex-col gap-1'}>
        <div className={'flex flex-col gap-2'}>
          <p className={'text-center font-sans text-[1.5rem] font-bold leading-[1.2] text-neutrals-black'}>
            {portalCopy.sign_up_mobile_code_title}
          </p>
          <p
            className={
              'flex flex-row flex-nowrap items-center justify-center gap-2 text-center font-sans text-[1.125rem] font-bold leading-[1.2] text-primary-blue'
            }
            data-role={'code-destination'}
          >
            {props.config.identity.formatted || props.config.identity.value}
            <Icon id="edit" />
          </p>
          {/* <button
            type={'button'}
            className={'paragraph-sm underline'}
            onClick={() => props.handleStateChange(ACCOUNT_SIGN_IN_STATES.INIT)}
          >
            {portalCopy.sign_up_mobile_code_change}
          </button> */}
        </div>
      </div>
      <div className={'flex w-full flex-1 flex-col items-center justify-center py-6 text-center'}>
        {/* <p className={'text-center h4 font-bold -tracking-[0.5px]'}>{portalCopy.sign_up_mobile_code_label}</p> */}
        <FieldCode
          placeholder={portalCopy.sign_up_mobile_code_placeholder}
          onInput={handleInput}
          value={code}
          error={error}
        />
      </div>
      {notice && (
        <p className={'paragraph-sm'} data-role={'code-notice'}>
          {notice}
        </p>
      )}
      <button
        className={'btn btn-lg btn-solid-black btn-inline relative w-full overflow-hidden px-4 py-2'}
        disabled={code.length !== 6 || submitting}
      >
        <span className={`${submitting ? 'invisible' : 'visible'}`}>{portalCopy.sign_up_mobile_code_submit}</span>
        <LoaderSpin wrapperClass={`bg-black ${submitting ? 'visible' : 'invisible'}`} />
      </button>
      <div className={'flex w-full flex-row items-center justify-center gap-2 py-3 text-center'}>
        <p className={'paragraph-sm'}>Didn’t Get a code?</p>
        <button
          type={'button'}
          className={'paragraph-sm w-fit font-bold underline disabled:opacity-90'}
          onClick={handleResend}
          disabled={cooldown > 0 || submitting}
          data-role={'code-resend'}
        >
          {cooldown > 0
            ? fill(portalCopy.sign_up_mobile_code_resend_wait, { seconds: cooldown })
            : portalCopy.sign_up_mobile_code_resend}
        </button>
      </div>
    </form>
  )
}

const AccountSignUpForm = ({ props }) => {
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(null)
  const [channel, setChannel] = useState(props.config.identity.channel)
  const [identifier, setIdentifier] = useState(props.config.identity.value)

  const isMobile = channel === SIGN_IN_CHANNELS.MOBILE

  const handleChannelChange = (next) => {
    if (next === channel) return
    setChannel(next)
    setIdentifier('')
    setError(null)
  }

  /**
   * An available identifier means no profile owns it yet, so the shopper is joining rather than
   * logging in and the flow continues to the details form.
   */
  async function handleSubmit(event) {
    event.preventDefault()
    if (!window.omneo?.portal) return

    const value = identifier.trim()
    if (!value) return

    setSubmitting(true)
    setError(null)

    const formatted = isMobile ? formatMobile(value) : value
    props.handleUpdateIdentity({ channel, value, formatted })

    const body = isMobile ? { mobile_phone: formatted } : { email: formatted }
    const response = await window.omneo.portal.checkAvailability(body)
    const availability = response?.data?.availability
    if (!availability) {
      setSubmitting(false)
      setError(authErrorMessage('unknown'))
      return
    }

    if (isMobile) {
      if (availability.mobile_phone?.available) {
        props.handleStateChange(ACCOUNT_SIGN_IN_STATES.DETAILS)
        return
      }
      const { ok, error: errorCode } = await window.omneo.portal.getSMSAuthCode(formatted)
      if (!ok) {
        setSubmitting(false)
        setError(authErrorMessage(errorCode))
        return
      }
      pushDataLayer('login_requested', { method: 'sms' })
      props.handleStateChange(ACCOUNT_SIGN_IN_STATES.MOBILE_CODE)
      return
    }

    if (availability.email?.available) {
      props.handleUpdateCustomer({ email: value })
      props.handleStateChange(ACCOUNT_SIGN_IN_STATES.DETAILS)
      return
    }

    await window.omneo.portal.sendLoginEmail(value, resolveLoginRedirect())
    pushDataLayer('login_requested', { method: 'email' })
    props.handleUpdateCustomer({
      id: availability.loyaltyCard,
      name: availability.email?.profile?.full_name
    })
    props.handleStateChange(ACCOUNT_SIGN_IN_STATES.SUCCESS)
  }

  const handleIdentifierChange = (event) => setIdentifier(event.currentTarget.value)

  return (
    <form
      data-ui={submitting ? 'visible' : ''}
      onSubmit={handleSubmit}
      className={
        'group/login relative flex h-full w-full flex-col gap-2 px-4 py-6 transition-opacity group-data-visible/login:opacity-45'
      }
    >
      <SignInChannelToggle channel={channel} onChange={handleChannelChange} disabled={submitting} />
      {/* Remounting on channel change clears the previous channel's value from the DOM input. */}
      <div className={'w-full py-4'} key={channel}>
        {isMobile ? (
          <FieldTel
            name={'identifier'}
            type={'tel'}
            autoComplete={'tel'}
            placeholder={portalCopy.sign_in_mobile_placeholder}
            value={identifier}
            onChange={handleIdentifierChange}
            onInput={handleIdentifierChange}
            error={error}
          />
        ) : (
          <FieldEmail
            name={'identifier'}
            placeholder={portalCopy.sign_in_email_placeholder}
            autoComplete={'email'}
            value={identifier}
            onChange={handleIdentifierChange}
            onInput={handleIdentifierChange}
            error={error}
          />
        )}
      </div>
      <button className={'btn btn-lg btn-solid-black btn-inline relative w-full overflow-hidden px-4 py-2'}>
        <span className={`flex flex-row items-center justify-center gap-2 ${submitting ? 'invisible' : 'visible'}`}>
          Continue <Icon id="arrow-right" className="" />
        </span>
        <LoaderSpin wrapperClass={`bg-black ${submitting ? 'visible' : 'invisible'}`} />
      </button>
      {/* The child element is the Character snippet render from liquid. */}
      {props.children}
    </form>
  )
}

const AccountSignUpSuccess = ({ props }) => {
  return (
    <div className={'flex flex-col gap-4'}>
      <div className="mb-4 flex flex-col items-center justify-center">
        <svg width="162" className="pointer-events-none" height="29" viewBox="0 0 162 29" fill="none" xmlns="http://www.w3.org/2000/svg">
          <g clip-path="url(#clip0_17082_258205)">
            <path
              d="M151.843 20.1873H154.369C155.858 20.1873 156.709 21.0676 156.709 22.1956C156.709 23.3236 155.858 24.2039 154.369 24.2039H151.843V20.1873ZM151.843 12.319H153.438C154.874 12.319 155.752 13.0343 155.752 14.2998C155.752 15.5654 154.874 16.2807 153.438 16.2807H151.843V12.319ZM158.437 17.6562C159.634 16.9409 160.804 15.5654 160.804 13.502C160.804 10.0906 158.411 7.88971 153.731 7.88971H146.764V28.6332H154.741C159.368 28.6332 162 26.2123 162 22.5808C162 20.2698 160.511 18.3715 158.437 17.6562ZM135.191 24.2039C133.25 24.2039 131.974 22.9109 131.974 20.2973V7.88971H126.762V20.2698C126.762 25.7996 130.405 28.9634 135.191 28.9634C139.978 28.9634 143.62 25.7996 143.62 20.2698V7.88971H138.409V20.2973C138.409 22.9109 137.132 24.2039 135.191 24.2039ZM113.272 28.6332H125.53V24.2039H118.484V7.88971H113.272V28.6332ZM100.376 18.2615C100.376 15.0151 102.663 12.6217 105.827 12.6217C107.794 12.6217 109.39 13.447 110.347 14.5199V8.54998C108.964 7.91722 107.342 7.55957 105.72 7.55957C99.8705 7.55957 95.1641 12.3741 95.1641 18.2615C95.1641 24.1489 99.8705 28.9634 105.72 28.9634C107.342 28.9634 108.964 28.6332 110.347 27.973V22.003C109.39 23.076 107.794 23.9013 105.827 23.9013C102.663 23.9013 100.376 21.5078 100.376 18.2615Z"
              fill="#389810"
            />
            <path d="M0 12.319H5.42442V28.6332H10.6361V12.319H16.0605V7.88965H0V12.319Z" fill="#389810" />
            <path
              d="M30.1521 15.8129H22.9461V7.88965H17.7344V28.6332H22.9461V20.2422H30.1521V28.6332H35.3638V7.88965H30.1521V15.8129Z"
              fill="#389810"
            />
            <path
              d="M50.7272 12.319V7.88965H38.2031V28.6332H50.7272V24.2039H43.3085V20.2422H50.4613V15.8129H43.3085V12.319H50.7272Z"
              fill="#389810"
            />
            <path
              d="M84.879 29C87.8807 29 90.0789 26.6707 90.4895 24.265C90.6077 23.565 89.7155 23.5283 89.3905 24.1091C89.0152 25.4296 87.8689 26.4384 86.2173 26.4384C84.8287 26.4384 84.0458 25.3135 84.4092 23.1768L86.3946 11.5303H91.5738C91.8752 11.5303 92.1765 11.298 92.2356 10.9465L92.3006 10.5582C92.3538 10.2464 92.1381 9.97744 91.8368 9.97744H86.6576L87.2485 6.29092C87.3341 5.78655 87.2307 5.51449 86.7817 5.51449H86.4064C86.0696 5.51449 85.7594 5.78655 85.6353 6.29092C85.071 8.50406 83.9572 9.97744 82.1549 9.97744H80.8047C80.4295 9.97744 80.202 10.2098 80.1429 10.5582L80.0779 10.9465C80.0189 11.2949 80.205 11.5303 80.5418 11.5303H83.0177L81.0381 23.137C80.3645 27.0956 81.9895 29 84.879 29ZM70.5763 28.6118H75.1912C75.6403 28.6118 76.0126 28.4161 76.0658 28.1074L76.0924 27.9515C76.2194 27.2148 74.7185 27.2148 74.949 25.8545L77.6021 10.2862C77.6937 9.74207 77.3864 9.35385 76.5326 9.74207L73.392 11.2185C73.0197 11.4141 72.7774 11.5303 72.7302 11.8024L72.7183 11.8788C72.6179 12.4596 73.9179 12.7714 73.6579 14.2845L71.6872 25.8545C71.4568 27.2118 70.1066 27.2118 69.9795 27.9515L69.9529 28.1074C69.8998 28.4192 70.1302 28.6118 70.5793 28.6118M76.5562 6.28787C78.0187 6.28787 79.1591 5.3158 79.4043 3.88215C79.6495 2.44545 78.8459 1.4367 77.3835 1.4367C75.8442 1.4367 74.6978 2.44545 74.4526 3.88215C74.2074 5.31886 75.0169 6.28787 76.5562 6.28787ZM69.56 0C65.5833 0 62.9686 2.79393 62.3186 6.59966L61.7424 9.97744H58.6284C58.2916 9.97744 58.0198 10.2495 57.9725 10.5216L57.8869 11.0259C57.8396 11.298 58.0257 11.5303 58.3625 11.5303H61.4765L59.0361 25.8545C58.7968 27.2515 57.3669 27.2912 57.2398 28.0279L57.2191 28.1441C57.1719 28.4161 57.3285 28.6087 57.7037 28.6087H62.9951C63.332 28.6087 63.5919 28.4131 63.6363 28.1441L63.6569 28.0279C63.784 27.2912 62.0999 27.2515 62.3392 25.8545L64.7797 11.5303H69.9943C70.2957 11.5303 70.5586 11.298 70.6059 11.0259L70.6916 10.5216C70.7388 10.2495 70.5586 9.97744 70.2602 9.97744H65.0456L65.2228 8.92895C65.1165 4.93064 66.2392 1.20438 68.8214 1.24107C72.154 1.28081 69.3177 5.16296 72.2427 5.16296C73.1438 5.16296 73.7494 4.46295 73.8617 3.80573C74.2192 1.70876 72.1836 0 69.557 0"
              fill="#389810"
            />
            <path
              d="M91.84 9.97724H86.6608L87.2517 6.28766C87.3374 5.78329 87.234 5.51123 86.7849 5.51123H86.4097C86.0729 5.51123 85.7626 5.78329 85.6385 6.28766C85.0742 8.50079 83.9604 9.97724 82.1582 9.97724H80.808C80.4328 9.97724 80.2053 10.2096 80.1462 10.558L80.0812 10.9462C80.0221 11.2947 80.2082 11.5301 80.545 11.5301H83.0209L81.0414 23.1368C80.3677 27.0954 81.9927 28.9998 84.8822 28.9998C87.7717 28.9998 90.0821 26.6705 90.4928 24.2648C90.5578 23.8888 90.3273 23.7054 90.0525 23.7054C89.8162 23.7054 89.5444 23.8429 89.3937 24.1119C89.0185 25.4325 87.8721 26.4412 86.2206 26.4412C84.832 26.4412 84.049 25.3163 84.4124 23.1796L86.3978 11.5332H91.577C91.8784 11.5332 92.1798 11.3008 92.2389 10.9493L92.3039 10.5611C92.357 10.2493 92.1414 9.98029 91.84 9.98029V9.97724Z"
              fill="#389810"
            />
            <path
              d="M77.1293 9.58594C76.9727 9.58594 76.7747 9.63485 76.5325 9.74184L73.3919 11.2183C73.0196 11.4139 72.7773 11.5301 72.7301 11.8021L72.7182 11.8785C72.6178 12.4624 73.9178 12.7711 73.6578 14.2843L71.6871 25.8543C71.4567 27.2146 70.1065 27.2146 69.9794 27.9513L69.9528 28.1072C69.8997 28.419 70.1301 28.6115 70.5792 28.6115H75.1941C75.6432 28.6115 76.0154 28.419 76.0686 28.1072L76.0952 27.9513C76.2222 27.2146 74.7214 27.2146 74.9518 25.8543L77.6049 10.2859C77.6729 9.89468 77.5311 9.58594 77.1293 9.58594Z"
              fill="#389810"
            />
          </g>
          <defs>
            <clipPath id="clip0_17082_258205">
              <rect width="162" height="29" fill="white" />
            </clipPath>
          </defs>
        </svg>
      </div>

      <div className={'flex flex-col gap-4'}>
        <p className={'text-xl font-sans text-center font-bold tracking-tight leading-tight'}>
          {accountTranslations.account_portal.sign_up_success_title}
        </p>
        <p className="paragraph-md text-center">
          {props.config.customer?.id
            ? accountTranslations.account_portal.sign_up_success_description
            : accountTranslations.account_portal.sign_up_not_activated_description}
        </p>
        {props.config.identity?.value && (
          <p className={'paragraph-sm text-center font-bold'} data-role={'success-destination'}>
            {fill(portalCopy.sign_up_success_destination, {
              destination: props.config.identity.formatted || props.config.identity.value
            })}
          </p>
        )}
      </div>
      {props.config.customer?.id && (
        <AccountFitClubCard
          memberSince={''}
          firstName={props.config.customer.name}
          barcode={props.config.customer.id}
          showTotal={false}
        />
      )}
    </div>
  )
}

const AccountSignUpDetails = ({ props }) => {
  const { formInputs, handleOnChange, handleSubmit, submitting, error } = useSignUpDetails({
    email: props.config.customer.email || '',
    mobile: props.config.identity?.channel === SIGN_IN_CHANNELS.MOBILE ? props.config.identity.value : '',
    collectComms: true,
    onSuccess: (customer) => {
      props.handleUpdateCustomer(customer)
      props.handleStateChange(ACCOUNT_SIGN_IN_STATES.SUCCESS)
    }
  })

  return (
    <div className={'absolute inset-0 z-[11] h-full w-full overflow-y-auto bg-white py-3'}>
      <div className={'flex w-full items-center justify-between border-b border-neutrals-medium-grey px-6 pb-3'}>
        <p className={'paragraph-lg font-bold'}>{props.dataSignUpTitle}</p>
        <button onClick={() => props.handleStateChange(ACCOUNT_SIGN_IN_STATES.INIT)} className={'flex flex-row gap-2'}>
          <Icon id="close" className="pointer-events-none size-5" />
        </button>
      </div>
      <form onSubmit={handleSubmit} className={'flex flex-col gap-6 px-6 py-8'}>
        {/* Name fields */}
        <div className={'flex flex-col gap-2 text-left'}>
          <p className={'paragraph-xs font-bold'}>{accountTranslations.form_labels.your_name}*</p>
          <div className={'flex flex-row gap-4'}>
            <FieldText
              type="text"
              autoComplete="given-name"
              required={true}
              placeholder={accountTranslations.form_placeholders.first_name}
              name={formInputs.firstName.key}
              value={formInputs.firstName.value}
              onChange={handleOnChange}
            />
            <FieldText
              type="text"
              autoComplete="family-name"
              required={true}
              placeholder={accountTranslations.form_placeholders.last_name}
              name={formInputs.lastName.key}
              value={formInputs.lastName.value}
              onChange={handleOnChange}
            />
          </div>
        </div>
        {/* Email field */}
        <FieldEmail
          label={accountTranslations.form_labels.email}
          autoComplete="email"
          placeholder={accountTranslations.form_placeholders.email}
          required={true}
          name={formInputs.email.key}
          value={formInputs.email.value}
          onChange={handleOnChange}
          error={error?.type === 'email' ? error.message : null}
        />
        {/* Mobile field */}
        {/* Optional by design: a shopper who joined with an email is not asked for a number. */}
        <FieldTel
          label={accountTranslations.form_labels.mobile_optional}
          autoComplete="tel"
          type="tel"
          placeholder={accountTranslations.form_placeholders.mobile}
          required={false}
          name={formInputs.mobile.key}
          value={formInputs.mobile.value}
          onChange={handleOnChange}
          error={error?.type === 'mobile_phone' ? error.message : null}
        />
        {/* Optional, and not yet sent anywhere - see docs/omneo-fit-club-auth-open-questions.md */}
        {/* HIDDEN FOR NOW */}
        {/* <FieldBirthday
          label={accountTranslations.form_labels.child_birthday}
          day={formInputs.childBirthDay}
          month={formInputs.childBirthMonth}
          year={formInputs.childBirthYear}
          onChange={handleOnChange}
        /> */}
        <div className={'flex flex-col gap-4'}>
          {/* Both consent rows are merchant-editable on the Login or Join Drawer section. */}
          <FieldConsent
            name={formInputs.marketingOptIn.key}
            value={formInputs.marketingOptIn.value}
            onChange={handleOnChange}
            html={props.dataMarketingConsent || accountTranslations.form_labels.marketing_consent_html}
          />
          <FieldConsent
            name={formInputs.termsAccepted.key}
            value={formInputs.termsAccepted.value}
            onChange={handleOnChange}
            required={true}
            html={props.dataTermsConsent || accountTranslations.form_labels.terms_consent_html}
          />
          {/* This is the old terms text use this if requested */}
          {/* <div
          className={'richtext richtext-accent-url paragraph-xs text-neutrals-secondary-grey'}
          dangerouslySetInnerHTML={{ __html: props.dataTerms }}
        ></div> */}
        </div>
        {/* Accepting the terms is what enables this; btn-solid-black already carries the disabled look. */}
        <button
          className={'btn btn-solid-black btn-lg relative mt-2 w-full justify-center gap-2'}
          disabled={!formInputs.termsAccepted.value || submitting}
        >
          {submitting ? <LoaderSpin /> : accountTranslations.form_labels.continue}
          {!submitting && <Icon id="arrow-right" className="my-auto" />}
        </button>

        {error?.message && <div className={'paragraph-sm font-medium text-system-error'}>{error.message}</div>}
      </form>
    </div>
  )
}

const AccountSignUpBody = ({ props }) => {
  switch (props.config.state) {
    case ACCOUNT_SIGN_IN_STATES.INIT: {
      return <AccountSignUpForm props={props} />
    }
    case ACCOUNT_SIGN_IN_STATES.MOBILE_CODE: {
      return <AccountMobileCodeValidation props={props} />
    }
    case ACCOUNT_SIGN_IN_STATES.DETAILS: {
      return <AccountSignUpDetails props={props} />
    }
    case ACCOUNT_SIGN_IN_STATES.SUCCESS: {
      return <AccountSignUpSuccess props={props} />
    }
  }
}

export const AccountSignUp = (props) => {
  /**
   * Which channel the drawer opens on, from the Login Drawer Channel A/B toggle. The section
   * resolves the treatment and puts it on the host element, so an unset or unknown value has to
   * mean email - that is the control, and a typo must not change what is being measured.
   */
  const defaultChannel =
    props.dataSignupChannel === SIGN_IN_CHANNELS.MOBILE ? SIGN_IN_CHANNELS.MOBILE : SIGN_IN_CHANNELS.EMAIL

  const [config, setConfig] = useState(() => ({
    state: ACCOUNT_SIGN_IN_STATES.INIT,
    customer: {},
    /** What the shopper logged in with, so the verify and success screens can name it. */
    identity: { channel: defaultChannel, value: '', formatted: '' },
    /* `?flow=` jumps straight to one screen for styling; null on every real visit. */
    ...resolveFlowPreview(ACCOUNT_SIGN_IN_STATES)
  }))

  /** Used on a click event and pulls the data-role attribute
   * from element to get the next state.
   */
  const handleStateChange = (state) => {
    setConfig((c) => ({
      ...c,
      state
    }))
  }

  const handleUpdateCustomer = (customer) => {
    setConfig((c) => ({
      ...c,
      customer
    }))
  }

  const handleUpdateIdentity = (identity) => {
    setConfig((c) => ({
      ...c,
      identity
    }))
  }

  /**
   * The drawer's marketing content (benefits list, footer link) is Liquid-rendered outside this
   * element, and only the identifier step shows it. Publishing the state onto the nearest
   * [data-account-flow] ancestor lets CSS hide it, which the previous approach did by deleting the
   * node outright - that also meant stepping back to the identifier form never brought it back.
   */
  const rootRef = useRef(null)
  useEffect(() => {
    const host = rootRef.current?.closest('[data-account-flow]')
    if (host) host.dataset.flowState = config.state
  }, [config.state])

  return (
    <div ref={rootRef} className={'h-full'}>
      <AccountSignUpBody
        props={{
          ...props,
          handleStateChange,
          handleUpdateCustomer,
          handleUpdateIdentity,
          config
        }}
      />
    </div>
  )
}

register(AccountSignUp, 'x-account-sign-in')
